import type { ReactNode } from 'react';
import { Download, Loader2, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Standard header for an admin list page: title + optional Refresh button
 * + optional Export CSV button + a `right` slot for any custom action.
 *
 * Centralises the placement so all 4 list pages look identical and the
 * "Refresh" interaction is consistent (icon + spinner state on reload).
 */
export function AdminListHeader({
  title,
  onRefresh,
  isRefreshing,
  onExportCsv,
  isExporting,
  right,
}: {
  title: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onExportCsv?: () => void;
  isExporting?: boolean;
  right?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h1 className="text-2xl font-semibold flex-1 min-w-0">{title}</h1>
      {right}
      {onRefresh && (
        <Button
          onClick={onRefresh}
          disabled={isRefreshing}
          variant="outline"
          size="sm"
          className="gap-1.5"
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      )}
      {onExportCsv && (
        <Button
          onClick={onExportCsv}
          disabled={isExporting}
          variant="outline"
          size="sm"
          className="gap-1.5"
        >
          {isExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export CSV
        </Button>
      )}
    </div>
  );
}

/**
 * Display chips for the currently active filters with a one-click X to
 * clear each. Hidden when no filters are active.
 */
export function ActiveFiltersBar({
  filters,
  onClearAll,
}: {
  filters: Array<{ key: string; label: string; onClear: () => void }>;
  onClearAll?: () => void;
}): JSX.Element | null {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">
        Active filters
      </span>
      {filters.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs pl-2.5 pr-1 py-0.5"
        >
          {f.label}
          <button
            type="button"
            onClick={f.onClear}
            className="rounded-full hover:bg-primary/20 p-0.5"
            aria-label={`Clear ${f.label}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {filters.length > 1 && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground underline ml-1"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
